/* eslint-disable max-len */
/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!document.getElementById('dependent-variant-option-styles')) {
  const dependentOptionStyles = document.createElement('style');
  dependentOptionStyles.id = 'dependent-variant-option-styles';
  dependentOptionStyles.textContent = `
variant-picker .option-selector[hidden],
variant-picker .opt-label[hidden],
variant-picker .custom-select__option[hidden],
variant-picker .js-mobile-size-select option[hidden] {
  display: none !important;
}
`;
  document.head.appendChild(dependentOptionStyles);
}

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.variant = this.getVariantData();
      this.variants = this.getVariantsData();
      this.lastValidOptions = this.getSelectedOptions();
      this.variantRequestId = 0;

      this.addListeners();
      this.updateVariantContent();
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        setTimeout(this.applySearchParams.bind(this), 0);
      }
      this.setAttribute('loaded', '');
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      this.updateAddToCartButton();
      this.updateSwatchLabel();
      this.updateAvailability();
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Handles 'mouseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return; // Already selected

      // Build option array if clicked
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      optionIds[inputOptionIndex] = input.dataset.valueId;

      // If all options are selected
      if (optionIds.indexOf(null) === -1) {
        // Wait to predict click-intent and preload link
        label.dataset.preloadTimeout = setTimeout(() => {
          label.dataset.preloaded = true;
          label.removeAttribute('data-preload-timeout');
          const combinedProductUrl = input.dataset.productUrl;
          const url = this.constructVariantUrl(
            combinedProductUrl,
            optionIds,
            input.dataset.variantId
          );
          theme.fetchCache.preload(url.toString());
        }, 250);
      }
    }

    /**
     * Handles 'mouseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      const sectionIds = [this.dataset.sectionId];
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((e) => {
          if (e.closest('.rte')) return;
          sectionIds.push(e.dataset.sectionId);
        });
      }
      url.searchParams.set('sections', sectionIds.join(','));
      if (combinedProductUrl || !variantId || optionIds.includes(null)) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    /**
     * Test whether a complete option set can remain selected after a change.
     * A sold-out variant is still a real selection, so shoppers can request
     * a restock notification for that exact combination.
     * @param {Array} options - Option values in product option order.
     * @param {number} changedIndex - Zero-based option index just changed.
     * @returns {boolean}
     */
    isValidOptionCombination(options, changedIndex) {
      if (!this.variants.length || options.includes(null)) return true;
      return Boolean(this.findVariantByOptions(options));
    }

    /**
     * Find the exact variant for a complete option set.
     * @param {Array} options - Option values in product option order.
     * @returns {?object}
     */
    findVariantByOptions(options) {
      if (options.includes(null)) return null;
      return this.variants.find((variant) => (
        variant.options.length === options.length
        && variant.options.every((value, index) => String(value) === String(options[index]))
      )) || null;
    }

    /**
     * Find the closest real variant while preserving the option just chosen.
     * @param {Array} options - Currently selected option values.
     * @param {number} changedIndex - Zero-based option index changed by the shopper.
     * @returns {?object}
     */
    findClosestVariant(options, changedIndex) {
      const changedValue = options[changedIndex];
      const candidates = this.variants.filter(
        (variant) => String(variant.options[changedIndex]) === String(changedValue)
      );
      const availableCandidates = candidates.filter((variant) => variant.available);
      const candidatePool = availableCandidates.length ? availableCandidates : candidates;

      return candidatePool.sort((left, right) => {
        const score = (variant) => variant.options.reduce((total, value, index) => (
          total + (index !== changedIndex && String(value) === String(options[index]) ? 10 : 0)
        ), variant.available ? 1 : 0);
        return score(right) - score(left);
      })[0] || null;
    }

    /**
     * Build a clear confirmation message for an invalid option combination.
     * @param {Array} selectedOptions - Values currently selected by the shopper.
     * @param {Array} resolvedOptions - Values from the closest real variant.
     * @param {number} changedIndex - Zero-based option index just changed.
     * @returns {string}
     */
    getInvalidCombinationMessage(selectedOptions, resolvedOptions, changedIndex) {
      const changes = resolvedOptions.reduce((list, nextValue, index) => {
        const currentValue = selectedOptions[index];
        if (
          index === changedIndex
          || currentValue == null
          || String(currentValue) === String(nextValue)
        ) {
          return list;
        }

        const optionName = this.optionSelectors[index]?.dataset.option || `Option ${index + 1}`;
        list.push(`${optionName} from “${currentValue}” to “${nextValue}”`);
        return list;
      }, []);

      if (!changes.length) return '';

      let formattedChanges = changes[0];
      if (changes.length === 2) {
        formattedChanges = `${changes[0]} and ${changes[1]}`;
      } else if (changes.length > 2) {
        formattedChanges = `${changes.slice(0, -1).join(', ')}, and ${changes[changes.length - 1]}`;
      }

      const changedOptionName = this.optionSelectors[changedIndex]?.dataset.option
        || `Option ${changedIndex + 1}`;
      return `That exact combination isn’t offered. Choosing “${selectedOptions[changedIndex]}” for ${changedOptionName} will also change ${formattedChanges}. Would you like to continue?`;
    }

    /**
     * Keep the mobile native Size dropdown aligned with the canonical radio
     * inputs used by the variant picker.
     */
    syncMobileSizeSelects() {
      this.querySelectorAll('.js-mobile-size-select').forEach((select) => {
        const selector = select.closest('.option-selector');
        if (!selector) return;

        const inputs = Array.from(selector.querySelectorAll('input.js-option'));
        const checkedInput = inputs.find((input) => input.checked);
        const selectedValue = checkedInput?.value || '';

        if (select.value !== selectedValue) {
          select.value = selectedValue;
        }

        const optionIndex = Number.parseInt(selector.dataset.index || '', 10) - 1;
        Array.from(select.options).forEach((option) => {
          if (!option.value) return;
          const input = inputs.find((candidate) => candidate.value === option.value);
          const status = input?.dataset.optionAvailability
            || (input?.classList.contains('is-unavailable') ? 'unavailable' : 'available');
          const shouldHide = optionIndex > 0 && status === 'unavailable';
          option.dataset.optionAvailability = status;
          option.hidden = shouldHide;
          option.disabled = shouldHide;
        });
      });
    }

    /**
     * Apply a real variant's values to every option selector without issuing
     * multiple network requests.
     * @param {Array} options - Variant option values.
     */
    applyOptionValues(options) {
      this.suppressVariantChange = true;
      this.optionSelectors.forEach((selector, index) => {
        const value = options[index];
        if (value == null) return;

        if (selector.dataset.selectorType === 'dropdown') {
          const option = selector.querySelector(
            `.custom-select__option[data-value="${CSS.escape(String(value))}"]`
          );
          if (option && option.getAttribute('aria-selected') !== 'true') {
            option.closest('custom-select')?.selectOption(option);
          }
        } else {
          const input = Array.from(selector.querySelectorAll('input.js-option')).find(
            (candidate) => candidate.value === String(value)
          );
          if (input) input.checked = true;
        }
      });
      this.updateSwatchLabel();
      this.suppressVariantChange = false;
      this.updateAvailability();
    }

    /**
     * Restore the last valid option set after a programmatic invalid change.
     */
    restoreLastValidOptions() {
      this.applyOptionValues(this.lastValidOptions);
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      if (this.suppressVariantChange) return;

      let changedControl = evt.target;
      const mobileSizeSelect = evt.target.closest?.('.js-mobile-size-select');
      if (mobileSizeSelect) {
        const selector = mobileSizeSelect.closest('.option-selector');
        const matchingInput = Array.from(selector?.querySelectorAll('input.js-option') || []).find(
          (input) => input.value === mobileSizeSelect.value
        );
        if (!matchingInput) return;
        matchingInput.checked = true;
        changedControl = matchingInput;
      }

      const changedSelector = changedControl.closest('.option-selector');
      const changedIndex = Number.parseInt(changedSelector?.dataset.index || '', 10) - 1;
      let selectedOptions = this.getSelectedOptions();
      if (!this.isValidOptionCombination(selectedOptions, changedIndex)) {
        const closestVariant = Number.isInteger(changedIndex)
          ? this.findClosestVariant(selectedOptions, changedIndex)
          : null;

        if (!closestVariant) {
          this.restoreLastValidOptions();
          return;
        }

        const confirmationMessage = this.getInvalidCombinationMessage(
          selectedOptions,
          closestVariant.options,
          changedIndex
        );
        if (
          confirmationMessage
          && changedIndex !== 0
          && !evt.detail?.noFocus
          && !window.confirm(confirmationMessage)
        ) {
          this.restoreLastValidOptions();
          return;
        }

        this.applyOptionValues(closestVariant.options);
        selectedOptions = this.getSelectedOptions();
      }
      this.lastValidOptions = selectedOptions;
      const resolvedVariant = this.findVariantByOptions(selectedOptions);
      this.variant = resolvedVariant || null;
      this.updateAddToCartButton();
      const pendingButton = this.getProductForm()?.querySelector('[name="add"]');
      if (pendingButton) pendingButton.disabled = true;

      const selectedOptionIds = this.getSelectedOptionIds();

      // Immediately update swatch label and compatible option states
      this.updateSwatchLabel();
      this.updateAvailability();

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = changedControl.dataset.productUrl || evt.detail?.productUrl;
      const variantId = resolvedVariant?.id
        || changedControl.dataset.variantId
        || evt.detail?.variantId;
      const url = this.constructVariantUrl(combinedProductUrl, selectedOptionIds, variantId);
      this.variantRequestId += 1;
      const currentRequestId = this.variantRequestId;

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (currentRequestId !== this.variantRequestId) return;

          const responseData = JSON.parse(responseText);

          // Update content
          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            let newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              if (!this.quickBuyContainer) {
                target = document.getElementById(sectionId);
              } else {
                const quickBuyTemplate = html.getElementById('quick-buy-template')?.content.querySelector('.js-product');
                if (quickBuyTemplate) newContent = quickBuyTemplate;
              }
            }

            this.updateContent(target, newContent, combinedProductUrl);
          });

          // Restore focus
          if (!evt.detail?.noFocus) {
            document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)?.focus();
          }

          // Update URL and announce changes
          const newVariantPicker = this.section.querySelector('variant-picker');
          setTimeout(() => newVariantPicker.announceChange(), 10);
        });
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      const selectedOptionIds = this.getSelectedOptionIds();
      if (!selectedOptionIds.includes(null)) {
        this.updateUrl();
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));
    }

    /**
     * Replace content when the variant or product changes.
     * @param {Element} target - Target container for replacing content within.
     * @param {document} newContent - New content containing elements to use.
     * @param {boolean} productChange - Product is changing.
     */
    updateContent(target, newContent, productChange) {
      // If something's not right, do nothing
      if (!target || !newContent) return;

      // Preselection - only update certain option value attributes
      if (this.getSelectedOptions().indexOf(null) !== -1 && !productChange) {
        target.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const newInput = newContent.querySelector(`.js-option[data-value-id="${input.dataset.valueId}"]`);
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          if (input.classList.contains('custom-select__option')) {
            input.innerHTML = newInput.innerHTML;
          }
        });
        this.updateStatusClasses();
        this.updateAvailability();
        return;
      }

      let replaceSelector = '[data-dynamic-variant-content]';
      if (productChange) {
        replaceSelector += ', [data-dynamic-product-content]';
      }
      const toReplaceList = target.querySelectorAll(replaceSelector);

      toReplaceList.forEach((toReplace) => {
        const replaceWith = toReplace.dataset.dynamicVariantContent
          ? newContent.querySelector(`[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`)
          : newContent.querySelector(`[data-dynamic-product-content="${CSS.escape(toReplace.dataset.dynamicProductContent)}"]`);
        if (!replaceWith) return;

        // Dispatch 'on:variant:before-replace-element' event before replacing element
        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: {
            toReplace,
            replaceWith
          }
        }));

        // If in quick buy, content needs mutating
        if (this.quickBuyContainer && theme.quickBuy) {
          theme.quickBuy.convertToQuickBuyContent(
            replaceWith,
            this.quickBuyContainer.dataset.productUrl
          );
        }

        // Transfer basic input values
        toReplace.querySelectorAll('input[id]:not([type="hidden"]), select[id]').forEach((sourceInput) => {
          if (sourceInput.matches('.custom-select__native')) {
            const customSelectId = sourceInput.id.replace('-native', '');
            const targetInput = replaceWith.querySelector(`#${CSS.escape(customSelectId)}`);
            const option = targetInput.querySelector(`.js-option[data-value="${CSS.escape(sourceInput.value)}"]`);
            setTimeout(() => {
              targetInput.selectOption(option);
            }, 0);
          } else {
            const targetInput = replaceWith.querySelector(`#${CSS.escape(sourceInput.id)}`);
            if (!targetInput) return;
            if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
              targetInput.toggleAttribute('checked', sourceInput.checked);
            } else {
              targetInput.value = sourceInput.value;
            }
          }
        });

        // Replace content
        const isIdInput = replaceWith.matches('input[name="id"]');
        if (isIdInput) {
          // Update ID input - preserving listeners
          [...replaceWith.attributes].forEach(
            (attr) => toReplace.setAttribute(attr.name, attr.value)
          );
          toReplace.value = replaceWith.value;
          // Defer change event until all forms are accurate
          setTimeout(() => {
            toReplace.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        } else {
          // Remember toReplace becomes detached
          toReplace.replaceWith(replaceWith);
        }

        // Do not mistake input value changes for variant changes - remove listeners
        replaceWith.querySelectorAll('variant-picker').forEach((el) => el.removeListeners());

        // Set values of any unselected <custom-select> elements again
        // A fetched variant may not appear selected
        const selectedOptionIds = this.getSelectedOptionIds();
        selectedOptionIds.forEach((optionId) => {
          const toSelect = replaceWith.querySelector(`.option-selector .custom-select__option[data-value-id="${optionId}"]:not([aria-selected="true"])`);
          if (toSelect) {
            setTimeout(() => {
              toSelect.closest('custom-select').selectOption(toSelect);
            }, 0);
          }
        });

        // Update variant picker after potential changes are applied
        setTimeout(() => {
          replaceWith.querySelectorAll('variant-picker').forEach((el) => {
            el.addListeners();
            el.updateVariantContent();
          });
        }, 10);
      });
    }

    /**
     * Set status classes to help with styling.
     */
    updateStatusClasses() {
      const selectedOpts = this.getSelectedOptionIds();
      const nullCount = selectedOpts.filter((x) => x === null).length;
      const isSingleNullAtEnd = nullCount === 1 && selectedOpts[selectedOpts.length - 1] === null;
      this.classList.toggle('variant-picker--preselection', nullCount > 0);
      this.classList.toggle('variant-picker--pre-last-selection', nullCount > 0 && !isSingleNullAtEnd);
    }

    /**
     * Copies the selected value into a swatch label. (Required for pre-selection.)
     */
    updateSwatchLabel() {
      this.querySelectorAll('.option-selector:has(.js-color-text)').forEach((option) => {
        const label = option.querySelector('.js-color-text');
        const input = option.querySelector('.js-option:is(:checked, [aria-selected="true"])');
        label.textContent = input ? input.value : '';
      });
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.variants.length) {
        const selectedOptions = this.getSelectedOptions();

        this.optionSelectors.forEach((selector, optionIndex) => {
          const optionInputs = Array.from(selector.querySelectorAll('.js-option'));

          // Combined-listing values can point to another product whose variants
          // are not included in this picker's JSON. Keep Shopify's server state.
          if (optionInputs.some((option) => option.dataset.productUrl)) return;

          optionInputs.forEach((option) => {
            const optionValue = option.dataset.value ?? option.value;
            if (optionValue == null || optionValue === '') return;

            const matchingVariants = this.variants.filter((variant) => (
              variant.options.length === this.optionSelectors.length
              && String(variant.options[optionIndex]) === String(optionValue)
              && variant.options.every((variantValue, variantIndex) => (
                variantIndex >= optionIndex
                || selectedOptions[variantIndex] == null
                || String(variantValue) === String(selectedOptions[variantIndex])
              ))
            ));
            const status = matchingVariants.some((variant) => variant.available)
              ? 'available'
              : matchingVariants.length > 0
                ? 'sold-out'
                : 'unavailable';
            const shouldHide = optionIndex > 0 && status === 'unavailable';

            option.dataset.optionAvailability = status;
            option.classList.toggle('is-unavailable', status === 'unavailable');
            option.classList.toggle('is-sold-out', status === 'sold-out');
            option.classList.toggle('is-nonexistent', status === 'unavailable');
            option.hidden = shouldHide;

            if (option.matches('input[type="radio"]')) {
              const label = selector.querySelector(`label[for="${CSS.escape(option.id)}"]`);
              if (label) label.hidden = shouldHide;
            }
          });

          const selectableOptions = optionInputs.filter((option) => (
            (option.dataset.value ?? option.value) !== ''
          ));
          selector.hidden = optionIndex > 0
            && selectableOptions.length > 0
            && !selectableOptions.some((option) => !option.hidden);
        });
      }

      this.syncMobileSizeSelects();

      if (this.dataset.availability === 'prune') {
        const toChange = this.querySelectorAll('.js-option.is-unavailable:is(:checked, [aria-selected="true"])');
        toChange.forEach((selected) => {
          const toSelect = selected.closest('.option-selector').querySelector('.js-option:not(.is-unavailable, [data-value=""])');
          if (toSelect) {
            setTimeout(() => {
              if (toSelect.closest('custom-select')) {
                toSelect.closest('custom-select').selectOption(toSelect);
              } else {
                toSelect.click();
              }
            }, 20); // Trigger after any updateContent logic has occurred
          }
        });
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      this.addBtn = productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;


      // Product not available
      if (!this.productAvailable) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Preselection
      if (this.getSelectedOptions().indexOf(null) !== -1) {
        if (this.addBtn.dataset.preselectionDisabled === 'true') {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.preselectionText;
        } else {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        }
        return;
      }

      // No variant
      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant;
        return;
      }

      // Sold out
      if (!this.variant.available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Add to cart
      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText;
    }

    /**
     * Updates the url with the selected variant id.
     */
    updateUrl() {
      if (this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ ...(window.history.state || {}) }, '', url);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('.js-selected-variant-data');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Gets all real variants for validating option combinations.
     * @returns {Array}
     */
    getVariantsData() {
      const dataEl = this.querySelector('.js-product-variants-data');
      return dataEl ? JSON.parse(dataEl.textContent) : [];
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            const convertEvent = (evt) => {
              evt.detail.noFocus = true;
              customSelect.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail })
              );
            };
            customSelect.addEventListener('change', convertEvent, { once: true });
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}

/* Dawson Jones — scaled glass shelf variant preview */
(() => {
  const imageByVariantId = {
  63975011778931: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-8x44-v1.png?v=1787075245",
  63975011811699: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-10x44-v1.png?v=1787075251",
  63975011844467: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-12x44-v1.png?v=1787075258",
  63975011877235: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-14x44-v1.png?v=1787075265",
  63965593076083: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-8x24.png?v=1786754179",
  63965593108851: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-8x36.png?v=1786754225",
  63965593141619: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-8x48.png?v=1786754234",
  63965593174387: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-8x56.png?v=1786754249",
  63965593207155: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-8x66.png?v=1786754274",
  63965593239923: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-10x24.png?v=1786754257",
  63965593272691: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-10x36.png?v=1786754242",
  63965593305459: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-10x48.png?v=1786754265",
  63965593338227: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-10x56.png?v=1786754282",
  63965593370995: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-10x66.png?v=1786754289",
  63965593403763: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-12x12.png?v=1786754298",
  63965593436531: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-12x24.png?v=1786754334",
  63965593469299: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-12x36.png?v=1786754347",
  63965593502067: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-12x48.png?v=1786754354",
  63965593534835: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-12x56.png?v=1786754361",
  63965593567603: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-12x66.png?v=1786754403",
  63966073848179: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-14x24.png?v=1786754420",
  63966073880947: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-14x36.png?v=1786754413",
  63966073913715: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-14x48.png?v=1786754395",
  63966073946483: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-14x56.png?v=1786754377",
  63966073979251: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-14x66.png?v=1786754386",
  63966093934963: "https://cdn.shopify.com/s/files/1/0979/2398/9875/files/glass-shelf-clear-14x14.png?v=1786754369"
};
  const shelfProductPath = '/products/clear-tempered-glass-shelf-3-16-thick';

  const setImageSource = (image, source, altText) => {
    const picture = image.closest('picture');
    picture?.querySelectorAll('source').forEach((element) => {
      element.srcset = source;
    });
    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');
    image.src = source;
    image.srcset = source;
    image.alt = altText;
  };

  const updateScaledShelfImage = (picker, variant) => {
    const variantId = String(variant?.id ?? '').replace('gid://shopify/ProductVariant/', '');
    const source = imageByVariantId[variantId];
    if (!source) return;

    const section = picker.closest('.shopify-section') || document;
    const gallery = section.querySelector('#product-media') || document.querySelector('#product-media');
    if (!gallery) return;

    const mainImages = gallery.querySelectorAll('.media-gallery__main img, .product-media__main img');
    const images = mainImages.length ? mainImages : gallery.querySelectorAll('img');
    const getDimension = (name) => {
      const namedValue = variant.selectedOptions?.find((option) => option.name === name)?.value;
      if (namedValue) return String(namedValue).trim();
      const selector = Array.from(picker.querySelectorAll('.option-selector[data-option]'))
        .find((element) => element.dataset.option === name);
      const position = Number(selector?.dataset.index);
      if (!Number.isInteger(position) || position < 1) return '';
      return String(variant.options?.[position - 1] ?? variant[`option${position}`] ?? '').trim();
    };
    const width = getDimension('Width');
    const length = getDimension('Length');
    const altText = width && length
      ? `Clear tempered glass shelf — ${width} × ${length}`
      : 'Clear tempered glass shelf';

    images.forEach((image) => setImageSource(image, source, altText));
  };

  document.addEventListener('on:variant:change', (event) => {
    const picker = event.target;
    if (
      !(picker instanceof HTMLElement) ||
      picker.tagName !== 'VARIANT-PICKER' ||
      !picker.dataset.url?.includes(shelfProductPath)
    ) return;

    const render = () => updateScaledShelfImage(picker, event.detail?.variant);
    render();
    window.setTimeout(render, 100);
    window.setTimeout(render, 300);
  });
})();


